import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axiosUserInstance from '../../../axiosUserInstance';
import axios from 'axios';
import Loader from '../../../Components/Loader/Loader';
import { FiMapPin, FiCalendar, FiStar, FiInfo, FiTag, FiChevronLeft, FiCheckCircle, FiAlertTriangle, FiX } from 'react-icons/fi';
import './EventDetails.css';
import omfLogo from '../../../assets/omfLogo.svg';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../../AuthProvider';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const PAYMENT_API = process.env.REACT_APP_PAYMENT_API_URL;

const parseJson = (raw, fallback) => {
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
};

const money = (n) => `$${parseFloat(n).toFixed(2)}`;

// Normalize price_options which may come as an object or a double-encoded JSON string
const normalizePriceOptions = (raw) => {
    if (!raw) return {};
    let val = raw;
    for (let i = 0; i < 2; i += 1) {
        if (typeof val === 'string') {
            try { val = JSON.parse(val); } catch { break; }
        }
    }
    return val && typeof val === 'object' ? val : {};
};

// Determine if a key is a valid ticket type key (only Single and Double)
const isTicketKeyName = (key) => {
    const lk = String(key).toLowerCase();
    return lk === 'single' || lk === 'double';
};

// Parse date strings safely: treat YYYY-MM-DD as a local date
const parseDateLocal = (s) => {
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = String(s).split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const dte = new Date(s);
    return Number.isNaN(dte?.getTime?.()) ? null : dte;
};

export default function EventDetails({ embed = false }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const { userID } = useAuth();
    const [loading, setLoading] = useState(false);
    const [event, setEvent] = useState(null);
    const [error, setError] = useState('');
    // Support multiple ticket types with independent quantities
    const [ticketQty, setTicketQty] = useState({}); // e.g., { single: 1, double: 2 }
    const [showSheet, setShowSheet] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [resultType, setResultType] = useState('success'); // 'success' | 'failure'
    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [phone, setPhone] = useState('');
    const [phoneTouched, setPhoneTouched] = useState(false);
    // Card details state and validation flags
    const [cardName, setCardName] = useState('');
    const [touched, setTouched] = useState({ name: false });
    const isValidEmail = useMemo(() => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email), [email]);
    const isValidCardName = useMemo(() => cardName.trim().length > 1, [cardName]);
    const isValidPhone = useMemo(() => {
        if (!phone) return true; // optional
        const digits = String(phone).replace(/\D/g, '');
        return digits.length >= 10 && digits.length <= 15;
    }, [phone]);
    // Phone is optional; do not gate Pay by phone validity
    const canPay = isValidEmail && isValidCardName;
    // Limit phone to max 10 digits; strip non-digits as user types
    const handlePhoneChange = (e) => {
        const digits = String(e?.target?.value || '').replace(/\D/g, '').slice(0, 10);
        setPhone(digits);
    };
    // Simple tax calc for events (no API): 16% of subtotal

    useEffect(() => {
        let mounted = true;
        const fetchEvent = async () => {
            try {
                setLoading(true);
                const res = await axiosUserInstance.get('/events/events', { skipAuth: true });
                const list = res?.data?.events || [];
                const found = list.find((e) => `${e.event_id}` === `${id}`);
                if (!mounted) return;
                if (!found) {
                    setError('Event not found');
                    return;
                }
                setEvent(found);
                const parsedPrices = normalizePriceOptions(found?.price_options);
                const filteredKeys = Object.keys(parsedPrices || {}).filter((k) => isTicketKeyName(k));
                // Initialize quantities to 0 for available types
                const init = {};
                filteredKeys.forEach((k) => { init[k] = 0; });
                setTicketQty(init);
            } catch (err) {
                if (!mounted) return;
                setError('Failed to load event');
                // eslint-disable-next-line no-console
                console.error('Event details fetch error', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchEvent();
        return () => {
            mounted = false;
        };
    }, [id]);

    const highlights = useMemo(() => {
        if (!event) return [];
        return Array.isArray(event.highlights) ? event.highlights : parseJson(event.highlights || '[]', []);
    }, [event]);

    const goodToKnow = useMemo(() => {
        if (!event) return {};
        return parseJson(event.good_to_know || '{}', {});
    }, [event]);

    const prices = useMemo(() => normalizePriceOptions(event?.price_options), [event]);
    const displayPrices = useMemo(() => {
        const res = {};
        Object.entries(prices || {}).forEach(([k, v]) => {
            if (!isTicketKeyName(k)) return;
            const p = parseFloat(v);
            if (Number.isFinite(p)) {
                res[k] = p; // include only valid numeric prices
            }
        });
        return res;
    }, [prices]);
    const totalQty = useMemo(() => Object.values(ticketQty || {}).reduce((s, n) => s + (parseInt(n, 10) || 0), 0), [ticketQty]);
    const subtotal = useMemo(() => {
        return Object.entries(ticketQty || {}).reduce((sum, [k, q]) => {
            const price = parseFloat(displayPrices?.[k] || 0);
            const qtyNum = parseInt(q, 10) || 0;
            return sum + price * qtyNum;
        }, 0);
    }, [ticketQty, displayPrices]);
    const total = Math.max(0, subtotal);
    const EVENTS_TAX_RATE = useMemo(() => {
        const r = parseFloat(process.env.REACT_APP_EVENTS_TAX_RATE);
        return Number.isFinite(r) && r >= 0 ? r : 0.16;
    }, []);
    const computedTax = useMemo(() => parseFloat(((total || 0) * EVENTS_TAX_RATE).toFixed(2)), [total, EVENTS_TAX_RATE]);
    const finalAmount = useMemo(() => parseFloat(((total || 0) + (computedTax || 0)).toFixed(2)), [total, computedTax]);

    const onCheckout = () => {
        if (totalQty <= 0 || total <= 0) return;
        setShowSheet(true);
    };

    // No API call for tax; computed from subtotal and rate.

    if (loading) return <Loader />;
    if (error)
        return (
            <div className="event-details-page">
                {!embed && (
                    <header className="ed-header">
                        <div className="ed-header__inner">
                            <Link to="/" className="ed-brand" aria-label="Oh My Food">
                                <img src={omfLogo} alt="Oh My Food" />
                                <span>Oh My Food</span>
                            </Link>
                            <nav className="ed-auth" aria-label="Authentication">
                                <Link to="/signup">Sign Up</Link>
                                <Link to="/login">Sign In</Link>
                            </nav>
                        </div>
                    </header>
                )}
                <div className="event-details__container">
                    <p className="error-text">{error}</p>
                </div>
            </div>
        );
    if (!event) return null;
    return (
        <Elements stripe={stripePromise}>
            <div className="event-details-page">
                {!embed && (
                    <header className="ed-header">
                        <div className="ed-header__inner">
                            <Link to="/" className="ed-brand" aria-label="Oh My Food">
                                <img src={omfLogo} alt="Oh My Food" />
                                <span>Oh My Food</span>
                            </Link>
                            <nav className="ed-auth" aria-label="Authentication">
                                <Link to="/signup">Sign Up</Link>
                                <Link to="/login">Sign In</Link>
                            </nav>
                        </div>
                    </header>
                )}
                {!embed && !userID && (
                    <div className="ed-banner">
                        <div className="ed-banner__inner">
                            <span className="ed-banner__text">Sign in to save 5% on your tickets</span>
                            <div className="ed-banner__actions">
                                <Link to="/login">Sign In</Link>
                                <Link to="/signup">Sign Up</Link>
                            </div>
                        </div>
                    </div>
                )}
                <div className="event-details__container">
                    <div className="event-details__topbar">
                        <button className="ed-back" aria-label="Back" onClick={() => navigate(-1)}>
                            <FiChevronLeft className="icon" />
                        </button>
                    </div>


                    {/* Hero image */}
                    {event?.media_url && (
                        <div
                            className="event-hero"
                            role="img"
                            aria-label={event?.title || 'Event image'}
                            style={{ backgroundImage: `url(${event.media_url})` }}
                        />
                    )}

                    <div className="event-details__grid">
                        <div className="event-details__content">
                            <h1 className="title">{event.title}</h1>
                            <div className="row">
                                <FiMapPin className="icon" />
                                {event?.location ? (
                                    <a className="map-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`} target="_blank" rel="noopener noreferrer">{event.location}</a>
                                ) : (
                                    <span>Location TBA</span>
                                )}
                            </div>
                            {(event.event_datetime || goodToKnow?.timing) && (
                                <div className="row">
                                    <FiCalendar className="icon green" />
                                    <span>
                                        {parseDateLocal(event.event_datetime)?.toLocaleDateString(undefined, {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                        })}
                                        {goodToKnow?.timing ? ` - ${goodToKnow.timing}` : ''}
                                    </span>
                                </div>
                            )}

                            

                            {highlights?.length > 0 && (
                                <div className="row">
                                    <FiStar className="icon yellow" />
                                    <span>{highlights.join(', ')}</span>
                                </div>
                            )}

                            {event.about_event && (
                                <div className="about">
                                    <h3><FiInfo className="icon" /> About</h3>
                                    <p>{event.about_event}</p>
                                </div>
                            )}

                            <div className="info">
                                <h3>
                                    <FiInfo className="icon" /> Good To Know
                                </h3>
                                <ul>
                                    {goodToKnow?.age_limit && <li>Age Limit: {goodToKnow.age_limit}+</li>}
                                    {goodToKnow?.non_refund_policy && <li>{goodToKnow.non_refund_policy}</li>}
                                    {goodToKnow?.parking_info && <li>Parking: {goodToKnow.parking_info}</li>}
                                </ul>
                            </div>
                        </div>

                        <aside className="event-details__checkout">
                            <div className="checkout-card">
                                <h3 className="checkout-title">
                                    <FiTag className="icon purple" /> Tickets
                                </h3>
                                <div className="ticket-types">
                                    {Object.keys(displayPrices).length === 0 && <div className="muted">No ticket options available.</div>}
                                    {Object.entries(displayPrices).map(([k, v]) => {
                                        const pretty = String(k).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
                                        const label = pretty;
                                        const qtyVal = parseInt(ticketQty?.[k] || 0, 10);
                                        return (
                                            <div key={k} className="ticket-type multi">
                                                <span className="label">{label}</span>
                                                <span className="price">{money(v)}</span>
                                                <div className="qty-controls">
                                                    <button onClick={() => setTicketQty((prev) => ({ ...prev, [k]: Math.max(0, (parseInt(prev?.[k] || 0, 10) - 1)) }))} disabled={qtyVal <= 0}>-</button>
                                                    <input type="number" min={0} value={qtyVal} onChange={(e) => setTicketQty((prev) => ({ ...prev, [k]: Math.max(0, parseInt(e.target.value || '0', 10)) }))} />
                                                    <button onClick={() => setTicketQty((prev) => ({ ...prev, [k]: (parseInt(prev?.[k] || 0, 10) + 1) }))}>+</button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Child tickets are disabled */}

                                <div className="summary">
                                    <div className="row">
                                        <span>Subtotal</span>
                                        <span>{money(total)}</span>
                                    </div>
                                    <div className="row muted">
                                        <span>Taxes & Other Fees</span>
                                        <span>{money(computedTax)}</span>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>

                    {/* Sticky pay bar */}
                    <div className="paybar">
                        <div className="paybar__total">Total: {money(finalAmount)}</div>
                        <button className="paybar__cta" disabled={totalQty <= 0 || total <= 0} onClick={onCheckout}>Proceed to Payment</button>
                    </div>

                    {/* Bottom sheet payment */}
                    {showSheet && (
                        <div className="payment-sheet__backdrop" onClick={() => { if (!isPaying) setShowSheet(false); }}>
                            <div className="payment-sheet" onClick={(e) => e.stopPropagation()}>
                                <div className="payment-sheet__header">
                                    <div className="payment-sheet__title">Card Details</div>
                                    <button className="payment-sheet__close" onClick={() => { if (!isPaying) setShowSheet(false); }}>X</button>
                                </div>
                                <div className="payment-sheet__content">
                                    <div className="payment-field">
                                        <label>Email (required)</label>
                                        <input
                                            type="email"
                                            placeholder="you@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            onBlur={() => setEmailTouched(true)}
                                        />
                                        {emailTouched && !isValidEmail && (
                                            <div className="error-msg">Enter a valid email address</div>
                                        )}
                                    </div>
                                    <div className="payment-field">
                                        <label>Phone (optional)</label>
                                        <input
                                            type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={10}
                                            placeholder="(555) 555-5555"
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            onBlur={() => setPhoneTouched(true)} />
                                        {phoneTouched && !isValidPhone && (
                                            <div className="error-msg">Enter a valid phone number</div>
                                        )}
                                    </div>
                                    <div className="payment-field">
                                        <label>Name on card</label>
                                        <input
                                            type="text"
                                            placeholder="John Doe"
                                            value={cardName}
                                            onChange={(e) => setCardName(e.target.value)}
                                            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                                        />
                                        {touched.name && !isValidCardName && (
                                            <div className="error-msg">Enter cardholder name</div>
                                        )}
                                    </div>
                                    <EventStripeForm
                                        canPay={canPay}
                                        cardName={cardName}
                                        email={email}
                                        phone={phone}
                                        finalAmount={finalAmount}
                                        total={total}
                                        totalQty={totalQty}
                                        lineItems={Object.entries(ticketQty || {}).filter(([, q]) => (parseInt(q, 10) || 0) > 0).map(([k, q]) => ({ type: k, qty: parseInt(q, 10) || 0, unitPrice: parseFloat(displayPrices?.[k] || 0) }))}
                                        eventId={event?.event_id}
                                        userId={userID}
                                        isPaying={isPaying}
                                        setIsPaying={setIsPaying}
                                        setShowSheet={setShowSheet}
                                        setShowResult={setShowResult}
                                        setResultType={setResultType}
                                    />
                                    {isPaying && (
                                        <div className="ed-processing">
                                            <div className="ed-spinner"></div>
                                            <span>Processing payment...</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {showResult && (
                        <div className="ed-modal-backdrop" onClick={() => setShowResult(false)}>
                            <div className="ed-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="ed-modal-header">
                                    <div className="ed-modal-title">
                                        {resultType === 'success' ? (
                                            <>
                                                <FiCheckCircle className="ed-modal-icon success" />
                                                <h3>Payment Successful!</h3>
                                            </>
                                        ) : (
                                            <>
                                                <FiAlertTriangle className="ed-modal-icon failure" />
                                                <h3>Payment Failed</h3>
                                            </>
                                        )}
                                    </div>
                                    <button className="ed-modal-close" onClick={() => setShowResult(false)} aria-label="Close">
                                        <FiX />
                                    </button>
                                </div>
                                <div className="ed-modal-body">
                                    {resultType === 'success' ? (
                                        <p>
                                            Your ticket for <strong>{event?.title}</strong> has been confirmed.
                                            <br />
                                            A confirmation email with your QR ticket has been sent to <strong>{email}</strong>.
                                        </p>
                                    ) : (
                                        <p>
                                            Something went wrong while processing your payment.
                                            <br />
                                            Please check your payment method and try again.
                                        </p>
                                    )}
                                </div>
                                <div className="ed-modal-actions">
                                    {resultType === 'failure' ? (
                                        <>
                                            <button className="ed-btn ed-btn-primary" onClick={() => { setShowResult(false); setShowSheet(true); }}>
                                                Retry Payment
                                            </button>
                                            <a className="ed-btn ed-btn-secondary" href="tel:8044819892">Contact Support: 804-481-9892</a>
                                        </>
                                    ) : (
                                        <button className="ed-btn ed-btn-primary" onClick={() => setShowResult(false)}>Close</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Elements>
    );
}

// Keep this component outside EventDetails to avoid remounting Elements on every parent re-render.
function EventStripeForm({
    canPay,
    cardName,
    email,
    phone,
    finalAmount,
    total,
    totalQty,
    lineItems,
    eventId,
    userId,
    isPaying,
    setIsPaying,
    setShowSheet,
    setShowResult,
    setResultType,
}) {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [cardComplete, setCardComplete] = useState(false);
    const secureForStripe = (typeof window !== 'undefined') && (window.isSecureContext || ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname));

    const handlePay = async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (!canPay || !stripe || !elements || !secureForStripe) return;
        setIsPaying(true);
        try {
            // Always read the Element at call time to avoid stale references
            const cardNumberElement = elements.getElement(CardNumberElement);
            if (!cardNumberElement) {
                setIsPaying(false);
                return;
            }
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: cardName,
                    email,
                    phone,
                },
            });
            if (error) {
                // eslint-disable-next-line no-console
                console.error('Stripe PM error', error);
                setResultType('failure');
                setShowResult(true);
                return;
            }
            const items = Array.isArray(lineItems) ? lineItems.filter((it) => (it?.qty || 0) > 0) : [];
            const labelFromType = (t) => String(t).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
            const summaryItems = items.map((it) => `${labelFromType(it.type)} * ${it.qty}`).join(' , ');
            const ticketTypeSummary = `TicketType : ${summaryItems} qunty ${totalQty}`;
            const lineItemsPayload = items.map((it) => ({ type: it.type, qty: it.qty, unitPriceCents: Math.round((it.unitPrice || 0) * 100) }));
            const payload = {
                amount: Math.round(Number(finalAmount || 0) * 100),
                currency: 'usd',
                paymentMethodId: paymentMethod.id,
                buyerName: cardName,
                buyerEmail: email,
                buyerPhone: phone || '',
                qty: totalQty,
                ticketTypeSummary,
                lineItems: lineItemsPayload,
                eventId,
            };
            if (userId) {
                payload.userId = userId;
            }
            await axios.post(`${PAYMENT_API}/payment/guestpayment/pay`, payload);
            setShowSheet(false);
            setResultType('success');
            setShowResult(true);
            // Redirect back to Events after success
            try {
                setTimeout(() => navigate('/events', { replace: true }), 1200);
            } catch (_) { /* no-op */ }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Event payment error', err);
            setResultType('failure');
            setShowResult(true);
        } finally {
            setIsPaying(false);
        }
    };

    return (
        <>
            <div className="payment-field">
                <label>Card number</label>
                <div className="stripe-input"><CardNumberElement onChange={(e) => setCardComplete(!!e?.complete)} /></div>
            </div>
            <div className="payment-row">
                <div className="payment-field">
                    <label>Expiry</label>
                    <div className="stripe-input"><CardExpiryElement /></div>
                </div>
                <div className="payment-field">
                    <label>CVC</label>
                    <div className="stripe-input"><CardCvcElement /></div>
                </div>
            </div>
            {!secureForStripe && (
                <div className="error-msg">For security, card payments require HTTPS. Please reload this page over https or use localhost for development.</div>
            )}
            <button
                className="payment-sheet__pay"
                disabled={!canPay || isPaying || !cardComplete || !secureForStripe}
                onClick={handlePay}
                aria-busy={isPaying}
                aria-live="polite"
            >
                {isPaying ? 'Processing…' : `Pay ${money(finalAmount || total)}`}
            </button>
        </>
    );
}










